import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Banknote, Loader2, Check, X, Zap, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const statusColors = { Unallocated: "bg-amber-50 text-amber-700 border-amber-200", "Partially Allocated": "bg-blue-50 text-blue-700 border-blue-200", "Fully Allocated": "bg-green-50 text-green-700 border-green-200" };
const approvalColors = { Pending: "bg-amber-50 text-amber-700 border-amber-200", Approved: "bg-green-50 text-green-700 border-green-200", Rejected: "bg-red-50 text-red-600 border-red-200" };
const methods = ["EFT", "Cash", "Cheque", "Other"];
const fmt = (v) => `R${(parseFloat(v) || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

export default function SupplierPaymentsPage() {
  const { id } = useParams();
  if (id === "new") return <CreatePayment />;
  if (id) return <PaymentDetail payId={id} />;
  return <PaymentList />;
}

function PaymentList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/supplier-payments?approval=${filter}&limit=50`).then(r => { setItems(r.data.payments); setTotal(r.data.total); }).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="supplier-payments-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Supplier Payments</h1><p className="text-sm text-gray-500 mt-1">{total} payment{total !== 1 ? "s" : ""}</p></div>
        <Button data-testid="record-supplier-payment-btn" onClick={() => navigate("/supplier-payments/new")} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />Record Payment</Button>
      </div>
      <div className="flex gap-2 mb-4">
        {["", "Pending", "Approved", "Rejected"].map(s => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-sm text-xs ${filter === s ? "bg-[#E63946] text-white" : ""}`}>{s || "All"}</Button>
        ))}
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : items.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Banknote className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No supplier payments</p></CardContent></Card>
      ) : (
        <Card className="border rounded-sm shadow-sm"><div className="overflow-x-auto">
          <table data-testid="supplier-payments-table" className="w-full text-sm">
            <thead><tr className="border-b bg-gray-50/50">
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Number</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Supplier</th>
              <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Amount</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Method</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Allocation</th>
              <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Approval</th>
            </tr></thead>
            <tbody>{items.map(p => (
              <tr key={p.payment_id} data-testid={`spay-row-${p.payment_id}`} className="border-b border-gray-100 hover:bg-gray-50/50 cursor-pointer" onClick={() => navigate(`/supplier-payments/${p.payment_id}`)}>
                <td className="p-3 font-medium text-[#E63946]">{p.payment_number}</td>
                <td className="p-3 text-gray-600">{p.payment_date}</td>
                <td className="p-3 text-gray-700">{p.supplier_name}</td>
                <td className="p-3 text-right font-mono">{fmt(p.amount)}</td>
                <td className="p-3"><Badge className="rounded-full text-xs bg-gray-100 text-gray-600">{p.payment_method}</Badge></td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${statusColors[p.status] || "bg-gray-100"}`}>{p.status}</Badge></td>
                <td className="p-3"><Badge className={`rounded-full text-xs border ${approvalColors[p.approval_status] || "bg-gray-100"}`}>{p.approval_status}</Badge></td>
              </tr>
            ))}</tbody>
          </table>
        </div></Card>
      )}
    </div>
  );
}

function CreatePayment() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", amount: "", payment_method: "EFT", payment_date: new Date().toISOString().split("T")[0], reference: "", notes: "" });
  const [expenses, setExpenses] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/suppliers?limit=500").then(r => setSuppliers(r.data.suppliers || [])); }, []);
  useEffect(() => {
    if (form.supplier_id) api.get(`/suppliers/${form.supplier_id}/outstanding-expenses`).then(r => { setExpenses(r.data.expenses || []); setAllocations({}); }).catch(() => setExpenses([]));
    else { setExpenses([]); setAllocations({}); }
  }, [form.supplier_id]);

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const paymentAmount = parseFloat(form.amount) || 0;
  const unallocated = paymentAmount - totalAllocated;

  const autoAllocate = () => {
    if (paymentAmount <= 0) { toast.error("Enter amount first"); return; }
    let remaining = paymentAmount;
    const a = {};
    for (const exp of expenses) {
      if (remaining <= 0.01) break;
      const bal = parseFloat(exp.balance) || 0;
      const alloc = Math.min(remaining, bal);
      if (alloc > 0) { a[exp.expense_id] = alloc.toFixed(2); remaining -= alloc; }
    }
    setAllocations(a);
    toast.success("Auto-allocated");
  };

  const submit = async () => {
    if (!form.supplier_id) { toast.error("Select supplier"); return; }
    if (paymentAmount <= 0) { toast.error("Enter amount"); return; }
    if (totalAllocated > paymentAmount + 0.01) { toast.error("Allocations exceed amount"); return; }
    const allocs = Object.entries(allocations).filter(([, v]) => parseFloat(v) > 0).map(([id, v]) => ({ expense_id: id, amount: parseFloat(v) }));
    setSaving(true);
    try {
      const r = await api.post("/supplier-payments", { ...form, amount: paymentAmount, allocations: allocs });
      toast.success(`Payment ${r.data.payment_number} submitted for approval`);
      navigate(`/supplier-payments/${r.data.payment_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <div data-testid="create-supplier-payment-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/supplier-payments")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="font-heading font-bold text-2xl text-[#1F2937]">Record Supplier Payment</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Supplier *</label>
          <Select value={form.supplier_id} onValueChange={v => setForm({...form, supplier_id: v})}>
            <SelectTrigger data-testid="spay-supplier-select" className="rounded-sm"><SelectValue placeholder="Select supplier..." /></SelectTrigger>
            <SelectContent>{suppliers.map(s => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.company_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Amount *</label><Input data-testid="spay-amount" type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" className="rounded-sm" min="0" step="0.01" /></div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Method *</label>
          <Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}><SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
        </div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Date *</label><Input type="date" value={form.payment_date} onChange={e => setForm({...form, payment_date: e.target.value})} className="rounded-sm" /></div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Reference</label><Input value={form.reference} onChange={e => setForm({...form, reference: e.target.value})} placeholder="Transaction reference" className="rounded-sm" /></div>
        <div><label className="text-xs font-medium text-gray-500 uppercase mb-1 block">Notes</label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="rounded-sm" /></div>
      </div>
      {form.supplier_id && (
        <Card className="border rounded-sm mb-6"><CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-sm">Allocate to Supplier Invoices</h3>
            <div className="flex gap-2"><Button size="sm" variant="outline" onClick={autoAllocate} className="rounded-sm h-8 text-xs"><Zap className="w-3 h-3 mr-1" />Auto-Allocate</Button><Button size="sm" variant="outline" onClick={() => setAllocations({})} className="rounded-sm h-8 text-xs">Clear</Button></div>
          </div>
          {expenses.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No outstanding expenses for this supplier</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Expense #</th><th className="p-2 text-left text-xs text-gray-500">Date</th><th className="p-2 text-right text-xs text-gray-500">Amount</th><th className="p-2 text-right text-xs text-gray-500">Balance</th><th className="p-2 w-32 text-xs text-gray-500">Allocate</th></tr></thead>
              <tbody>{expenses.map(exp => (
                <tr key={exp.expense_id} className="border-b border-gray-100">
                  <td className="p-2 font-medium text-[#E63946]">{exp.expense_number}</td><td className="p-2 text-gray-600">{exp.expense_date}</td>
                  <td className="p-2 text-right font-mono">{fmt(exp.amount)}</td><td className="p-2 text-right font-mono">{fmt(exp.balance)}</td>
                  <td className="p-2"><Input type="number" value={allocations[exp.expense_id] || ""} onChange={e => setAllocations({...allocations, [exp.expense_id]: e.target.value})} className="rounded-sm h-8 text-sm text-right" min="0" max={exp.balance} step="0.01" /></td>
                </tr>
              ))}</tbody>
              <tfoot>
                <tr className="border-t"><td colSpan="4" className="p-2 text-right text-xs text-gray-500">Total Allocated:</td><td className="p-2 text-right font-mono font-medium text-green-600">{fmt(totalAllocated)}</td></tr>
                <tr><td colSpan="4" className="p-2 text-right text-xs text-gray-500">Unallocated:</td><td className={`p-2 text-right font-mono font-medium ${unallocated < 0 ? "text-red-500" : "text-amber-600"}`}>{fmt(Math.max(0, unallocated))}</td></tr>
              </tfoot>
            </table>
          )}
        </CardContent></Card>
      )}
      <p className="text-xs text-amber-600 mb-4">This payment will require manager/admin approval before being finalized.</p>
      <div className="flex gap-3">
        <Button data-testid="save-supplier-payment-btn" onClick={submit} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}Submit for Approval</Button>
        <Button variant="outline" onClick={() => navigate("/supplier-payments")} className="rounded-sm">Cancel</Button>
      </div>
    </div>
  );
}

function PaymentDetail({ payId }) {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const load = useCallback(() => { setLoading(true); api.get(`/supplier-payments/${payId}`).then(r => setP(r.data)).catch(() => toast.error("Not found")).finally(() => setLoading(false)); }, [payId]);
  useEffect(() => { load(); }, [load]);

  const approve = async () => { setSubmitting(true); try { await api.post(`/supplier-payments/${payId}/approve`); toast.success("Payment approved — expenses updated"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } setSubmitting(false); };
  const reject = async () => { if (!window.confirm("Reject this payment?")) return; setSubmitting(true); try { await api.post(`/supplier-payments/${payId}/reject`, { reason: "Rejected by admin" }); toast.success("Payment rejected"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } setSubmitting(false); };

  if (loading) return <div className="animate-pulse h-60 bg-gray-100 rounded-sm" />;
  if (!p) return <p>Not found</p>;

  return (
    <div data-testid="supplier-payment-detail">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/supplier-payments")} className="rounded-sm"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1"><h1 className="font-heading font-bold text-2xl text-[#1F2937]">{p.payment_number}</h1><p className="text-sm text-gray-500">{p.supplier_name}</p></div>
        <Badge className={`rounded-full text-sm border px-3 py-1 ${approvalColors[p.approval_status] || "bg-gray-100"}`}>{p.approval_status}</Badge>
      </div>
      {p.approval_status === "Pending" && (
        <div className="flex gap-2 mb-6">
          <Button data-testid="approve-spay-btn" size="sm" onClick={approve} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Check className="w-4 h-4 mr-1.5" />Approve</Button>
          <Button data-testid="reject-spay-btn" size="sm" variant="outline" onClick={reject} disabled={submitting} className="rounded-sm text-red-500 border-red-300"><X className="w-4 h-4 mr-1.5" />Reject</Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border rounded-sm"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Total Paid</p><p className="text-2xl font-bold font-mono text-[#1F2937]">{fmt(p.amount)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Allocated</p><p className="text-2xl font-bold font-mono text-green-600">{fmt(p.allocated_amount)}</p></CardContent></Card>
        <Card className="border rounded-sm border-amber-200"><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 uppercase font-semibold mb-1">Unallocated</p><p className="text-2xl font-bold font-mono text-amber-600">{fmt(p.unallocated_amount)}</p></CardContent></Card>
        <Card className="border rounded-sm"><CardContent className="p-4 space-y-1.5"><p className="text-xs text-gray-500 uppercase font-semibold">Details</p><p className="text-sm"><span className="text-gray-500">Date:</span> {p.payment_date}</p><p className="text-sm"><span className="text-gray-500">Method:</span> {p.payment_method}</p>{p.reference && <p className="text-sm"><span className="text-gray-500">Ref:</span> {p.reference}</p>}</CardContent></Card>
      </div>
      {p.allocations?.length > 0 && (
        <Card className="border rounded-sm mb-6"><CardContent className="p-4"><h3 className="font-heading font-semibold text-sm mb-3">Allocations</h3>
          <table className="w-full text-sm"><thead><tr className="border-b"><th className="p-2 text-left text-xs text-gray-500">Expense</th><th className="p-2 text-right text-xs text-gray-500">Amount</th></tr></thead>
            <tbody>{p.allocations.map(a => <tr key={a.allocation_id} className="border-b border-gray-100"><td className="p-2 font-medium">{a.expense_number}</td><td className="p-2 text-right font-mono">{fmt(a.amount)}</td></tr>)}</tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
}
